import { useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import {
    listAppWorkers,
    loadMoreAppWorkers
} from "../Actions/API/WorkerActions"
import { APIAppWorker } from "../Models/API/AppWorker"
import { FetchState } from "../Models/Client/FetchState"
import { getItemList, getPageToFetch } from "../Models/Client/RemoteData"
import { SortExpression } from "../Models/Client/SortExpression"
import { Result } from "../Models/Utility/Result"
import { MainThunkDispatch } from "../Reducers"
import { useAppState } from "./useReduxState"

export function useAppWorkerList(
    appID: string,
    sort: SortExpression<APIAppWorker>
): [
    FetchState<{
        items: APIAppWorker[]
        total: number
    }>,
    () => Promise<void>
] {
    const entry = useAppState(state => state.api.workers.app[appID])
    const dispatch = useDispatch<MainThunkDispatch>()
    const handleLoadMore = useCallback(
        () => dispatch(loadMoreAppWorkers(appID)),
        [appID, dispatch]
    )

    useEffect(() => {
        const page = entry === undefined ? 0 : getPageToFetch(entry, sort)

        if (
            page >= 0 ||
            JSON.stringify(sort) !== JSON.stringify(entry?.list?.params)
        ) {
            dispatch(listAppWorkers(appID, page, sort))
        }
    }, [dispatch, entry, sort, appID])

    return [
        useMemo(() => {
            if (entry === undefined || entry.list === undefined) {
                return undefined
            }

            return Result.ok({
                items: getItemList(entry),
                total: entry.list.total
            })
        }, [entry]),
        handleLoadMore
    ]
}
